import type { TFunction } from "i18next";
import { useState } from "react";
import { Link } from "react-router-dom";
import BrandWordmark from "./BrandWordmark";
import { Trans, useTranslation } from "./i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import PwaInstallButton from "./PwaInstallButton";

type AuthPageProps = {
  configured: boolean;
  signedOut?: boolean;
  onGoogle: () => Promise<void>;
  onLocalPreview?: () => void;
};

function authMessage(error: unknown, t: TFunction<"auth">) {
  const message = error instanceof Error ? error.message : t("errors.unavailable");
  if (/rate limit/i.test(message)) return t("errors.rateLimit");
  return message;
}

export default function AuthPage({ configured, signedOut, onGoogle, onLocalPreview }: AuthPageProps) {
  const { t } = useTranslation("auth");
  const { t: brandText } = useTranslation("brand");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function google() {
    if (busy) return;
    try {
      setBusy(true);
      setError("");
      await onGoogle();
    } catch (caught) {
      setError(authMessage(caught, t));
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell" id="main-content">
      <header className="auth-header">
        <Link className="brand-home brand-home-auth" to="/" aria-label={brandText("home")}>
          <BrandWordmark />
        </Link>
        <div className="language-header-actions">
          <LanguageSwitcher />
          <Link className="auth-home-link" to="/">
            <span aria-hidden="true">←</span> {t("backHome")}
          </Link>
        </div>
      </header>
      <div className="auth-layout">
        <section className="auth-brand-panel" aria-label={t("brandLabel")}>
          <div className="auth-introduction">
            <h1>
              <Trans t={t} i18nKey="heroTitle" />
            </h1>
          </div>
          <ul>
            <li>{t("benefits.account")}</li>
            <li>{t("benefits.localData")}</li>
            <li>{t("benefits.backup")}</li>
          </ul>
        </section>
        <section className="auth-card" aria-labelledby="auth-title">
          <div className="auth-card-head">
            <small>{t("teacherBeta")}</small>
            <h2 id="auth-title">{t("signin.title")}</h2>
          </div>
          {signedOut && (
            <p className="auth-config-note" role="status">
              {t("signedOutNotice")}
            </p>
          )}
          {!configured && (
            <p className="auth-config-note" role="status">
              <Trans t={t} i18nKey="previewNotice" components={{ preview: <b /> }} />
            </p>
          )}
          <button
            type="button"
            className="google-auth-button"
            disabled={!configured || busy}
            onClick={() => void google()}
          >
            <svg className="auth-google-icon" aria-hidden="true" viewBox="0 0 24 24">
              <path
                fill="#4285f4"
                d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.36Z"
              />
              <path
                fill="#34a853"
                d="M12 22c2.7 0 4.96-.9 6.62-2.41l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.61 0-4.83-1.76-5.62-4.13H3.03v2.59A10 10 0 0 0 12 22Z"
              />
              <path fill="#fbbc05" d="M6.38 13.91a6 6 0 0 1 0-3.82V7.5H3.03a10 10 0 0 0 0 9l3.35-2.59Z" />
              <path
                fill="#ea4335"
                d="M12 5.96c1.47 0 2.79.5 3.82 1.5l2.86-2.87A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.97 5.5l3.35 2.59A5.98 5.98 0 0 1 12 5.96Z"
              />
            </svg>
            {busy ? t("googleBusy") : t("google")}
          </button>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="auth-local-notice">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m12 3 8 3v5c0 5-4 8-8 10-4-2-8-5-8-10V6l8-3Z" />
              <path d="m8.5 12 2.5 2.5 4.5-5" />
            </svg>
            <div>
              <p>{t("localDataNotice")}</p>
              <p>{t("accessPrivacyNotice")}</p>
            </div>
          </div>
          <div className="pwa-install-entry">
            <b>{t("installPrompt")}</b>
            <PwaInstallButton />
          </div>
          {onLocalPreview && (
            <button type="button" className="auth-preview-button" onClick={onLocalPreview}>
              {t("localPreview")}
            </button>
          )}
          <p className="auth-legal">
            <Trans
              t={t}
              i18nKey="legal"
              components={{ terms: <Link to="/terms" />, privacy: <Link to="/privacy" /> }}
            />
          </p>
          <p className="auth-legal">
            <Link to="/support">{t("supportLink")}</Link>
          </p>
        </section>
      </div>
    </main>
  );
}
